<template>
  <section class="news-article-page page-stack">
    <article class="card page-hero-card news-hero">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">{{ articleSourceLabel }} article</p>
          <h2>{{ article.title || 'Loading article...' }}</h2>
          <!-- <p>{{ article.description || 'Reading the source article from Wowhead.' }}</p> -->
        </div>
      </header>
    </article>

    <div class="card news-article-actions">
      <RouterLink class="btn-outline" to="/news">
        <i class="fa-solid fa-arrow-left"></i>
        News
      </RouterLink>
      <div v-if="!isNativeLanguageArticle" class="language-dropdown" :class="{ 'is-open': languageMenuOpen }" aria-label="News language">
        <button
          class="btn-sm-outline language-dropdown-trigger"
          type="button"
          :disabled="articleTranslating"
          aria-haspopup="menu"
          :aria-expanded="languageMenuOpen"
          @click.stop="languageMenuOpen = !languageMenuOpen"
        >
          <i class="fa-solid fa-language"></i>
          {{ activeLanguageLabel }}
          <i class="fa-solid fa-chevron-down"></i>
        </button>
        <Transition name="favorite-menu">
          <div v-if="languageMenuOpen" class="language-dropdown-menu" role="menu" @click.stop>
            <button
              v-for="language in languages"
              :key="language.value"
              type="button"
              role="menuitem"
              :class="{ 'is-active': activeLanguage === language.value }"
              @click="selectLanguage(language.value)"
            >
              <span>{{ language.name }}</span>
              <i v-if="activeLanguage === language.value" class="fa-solid fa-check"></i>
            </button>
          </div>
        </Transition>
      </div>
      <span v-if="!isNativeLanguageArticle" class="translation-provider-chip">
        <i class="fa-solid fa-bolt"></i>
        {{ translationProviderLabel }}
      </span>
      <span v-else class="source-language-note source-language-note-compact">
        <i class="fa-solid fa-language" aria-hidden="true"></i>
        Original Ukrainian
      </span>
      <a v-if="article.url" class="btn-outline" :href="article.url" target="_blank" rel="noreferrer">
        <i class="fa-solid fa-arrow-up-right-from-square"></i>
        Source
      </a>
    </div>

    <div class="news-article-layout">
      <main class="news-article-main">
        <article v-if="loading" class="card news-article-shell">
          <span class="skeleton-media news-article-hero-skeleton"></span>
          <span class="skeleton-line skeleton-line-medium"></span>
          <span class="skeleton-line"></span>
          <span class="skeleton-line"></span>
          <span class="skeleton-line skeleton-line-short"></span>
        </article>

        <AppEmptyState
          v-else-if="error"
          eyebrow="Article"
          icon="fa-solid fa-triangle-exclamation"
          title="Unable to load the article"
          :message="error"
          :actions="[{ to: '/news', label: 'Back to News', icon: 'fa-solid fa-arrow-left' }]"
        />

        <article v-else class="card news-article-shell">
          <img
            v-if="article.imageUrl && !articleBannerFailed"
            class="news-article-banner"
            :src="article.imageUrl"
            :alt="article.title"
            loading="lazy"
            @error="handleArticleBannerError"
          >
          <div v-else class="news-article-banner news-article-banner-fallback" role="img" :aria-label="article.title">
            <i class="fa-solid fa-newspaper"></i>
            <span>{{ articleSourceLabel }}</span>
            <strong>{{ article.title }}</strong>
          </div>

          <header v-if="article.author || article.publishedAt" class="news-article-meta">
            <span v-if="article.author">{{ article.author }}</span>
            <time v-if="article.publishedAt" :datetime="article.publishedAt">{{ formatDate(article.publishedAt) }}</time>
          </header>

          <NewsArticleContent :blocks="articleBlocks" @open-source-article="openRelatedArticle" />
        </article>
      </main>

      <aside class="card news-other-posts">
        <header>
          <p class="eyebrow">Latest</p>
          <h3>Other posts</h3>
        </header>
        <div v-if="otherPostsLoading" class="news-other-posts-list">
          <span v-for="index in 5" :key="index" class="skeleton-line"></span>
        </div>
        <div v-else class="news-other-posts-list">
          <RouterLink v-for="post in otherPosts" :key="post.id" :to="articleRoute(post)">
            <span>{{ post.category }}</span>
            <strong>{{ post.title }}</strong>
            <time v-if="post.publishedAt" :datetime="post.publishedAt">{{ formatDate(post.publishedAt) }}</time>
          </RouterLink>
        </div>
      </aside>
    </div>

    <Transition name="app-modal">
      <div
        v-if="relatedArticleOpen"
        class="app-modal-backdrop news-related-article-backdrop"
        @click="closeRelatedArticle"
      >
        <section
          class="app-modal news-related-article-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="related-article-title"
          @click.stop
        >
          <header class="modal-head">
            <div>
              <p class="eyebrow">{{ articleSourceLabel }} article</p>
              <h2 id="related-article-title">{{ relatedArticle.title || 'Loading article...' }}</h2>
            </div>
            <div class="modal-actions">
              <a
                v-if="relatedArticle.url"
                class="btn-icon-outline"
                :href="relatedArticle.url"
                target="_blank"
                rel="noreferrer"
                aria-label="Open source"
                title="Open source"
              >
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
              </a>
              <button class="btn-icon-outline" type="button" aria-label="Close article" @click="closeRelatedArticle">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
          </header>

          <div class="news-related-article-scroll">
            <article v-if="relatedArticleLoading" class="news-related-article-loading">
              <span class="skeleton-media"></span>
              <span class="skeleton-line"></span>
              <span class="skeleton-line"></span>
              <span class="skeleton-line skeleton-line-medium"></span>
            </article>
            <AppEmptyState
              v-else-if="relatedArticleError"
              eyebrow="Article"
              icon="fa-solid fa-triangle-exclamation"
              title="Unable to load the article"
              :message="relatedArticleError"
            />
            <article v-else>
              <img
                v-if="relatedArticle.imageUrl"
                class="news-related-article-banner"
                :src="relatedArticle.imageUrl"
                :alt="relatedArticle.title"
                loading="lazy"
              >
              <NewsArticleContent
                :blocks="relatedArticleBlocks"
                @open-source-article="openRelatedArticle"
              />
            </article>
          </div>
        </section>
      </div>
    </Transition>
  </section>
</template>

<script>
import AppEmptyState from '../components/AppEmptyState.vue'
import NewsArticleContent from '../components/NewsArticleContent.vue'
import { getNewsArticle, getWowheadFeedPosts } from '../services/wowheadNews'
import {
  getLastTranslationProvider,
  mapWithConcurrency,
  startTranslationCollection,
  stopTranslationCollection,
  summarizeTranslationProviders,
  setTranslationProvider,
  translateText,
  translateTextSegments,
} from '../services/translation'
import { formatNewsDate } from '../utils/formatters'

const NEWS_LANGUAGE_KEY = 'rio-news-language'

function loadNewsLanguage() {
  try {
    const language = localStorage.getItem(NEWS_LANGUAGE_KEY)
    return ['en', 'uk', 'ru'].includes(language) ? language : 'en'
  } catch {
    return 'en'
  }
}

export default {
  name: 'NewsArticleView',
  components: {
    AppEmptyState,
    NewsArticleContent,
  },
  data() {
    return {
      article: {
        title: '',
        description: '',
        imageUrl: '',
        author: '',
        publishedAt: '',
        url: '',
        blocks: [],
      },
      loading: false,
      error: '',
      otherPosts: [],
      otherPostsLoading: false,
      articleBannerFailed: false,
      relatedArticleOpen: false,
      relatedArticleLoading: false,
      relatedArticleError: '',
      relatedArticleRequestId: 0,
      relatedArticle: {
        title: '',
        imageUrl: '',
        url: '',
        blocks: [],
      },
      sourceArticle: null,
      articleRequestId: 0,
      languageRequestId: 0,
      articleTranslating: false,
      activeLanguage: loadNewsLanguage(),
      languageMenuOpen: false,
      translationProvider: getLastTranslationProvider(),
      languages: [
        { value: 'en', label: 'EN', name: 'English' },
        { value: 'uk', label: 'UA', name: 'Українська' },
        { value: 'ru', label: 'RU', name: 'Русский' },
      ],
    }
  },
  computed: {
    articleUrl() {
      return String(this.$route.query.url || '')
    },
    articleSource() {
      return this.$route.query.source === 'wowhub' ? 'wowhub' : 'wowhead'
    },
    articleSourceLabel() {
      return this.articleSource === 'wowhub' ? 'WoW Hub' : 'Wowhead'
    },
    // WoW Hub publishes in Ukrainian and the translation providers only translate from English,
    // so these articles stay in their original language and the language picker is hidden.
    isNativeLanguageArticle() {
      return this.articleSource === 'wowhub'
    },
    activeLanguageLabel() {
      return this.languages.find((language) => language.value === this.activeLanguage)?.label || 'EN'
    },
    translationProviderLabel() {
      if (this.activeLanguage === 'en') return 'No translate'
      if (this.articleTranslating) return 'In process...'
      return this.formatTranslationProvider(this.translationProvider)
    },
    articleBlocks() {
      let skippedBanner = false

      return (this.article.blocks || []).filter((block) => {
        if (
          !skippedBanner
          && block.type === 'image'
          && this.article.imageUrl
          && block.src === this.article.imageUrl
        ) {
          skippedBanner = true
          return false
        }

        return true
      })
    },
    relatedArticleBlocks() {
      let skippedBanner = false

      return (this.relatedArticle.blocks || []).filter((block) => {
        if (
          !skippedBanner
          && block.type === 'image'
          && this.relatedArticle.imageUrl
          && block.src === this.relatedArticle.imageUrl
        ) {
          skippedBanner = true
          return false
        }

        return true
      })
    },
  },
  watch: {
    articleUrl: {
      immediate: true,
      handler() {
        this.closeRelatedArticle()
        this.loadArticle()
        this.loadOtherPosts()
      },
    },
    articleSource() {
      this.loadArticle()
      this.loadOtherPosts()
    },
    activeLanguage() {
      this.persistNewsLanguage()
      this.applyArticleLanguage()
    },
  },
  mounted() {
    document.addEventListener('click', this.closeLanguageMenu)
    document.addEventListener('keydown', this.handleDocumentKeydown)
    window.addEventListener('rio:translation-provider', this.updateTranslationProvider)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeLanguageMenu)
    document.removeEventListener('keydown', this.handleDocumentKeydown)
    window.removeEventListener('rio:translation-provider', this.updateTranslationProvider)
  },
  methods: {
    updateTranslationProvider(event) {
      this.translationProvider = event.detail || null
    },
    formatTranslationProvider(provider) {
      if (provider?.status === 'limit') return 'Limit reached'

      const label = provider?.label || 'Unknown provider'
      const queue = provider?.index && provider?.total ? ` - ${provider.index}/${provider.total}` : ''
      const cache = provider?.cached ? 'Cache - ' : ''
      return `${cache}${label}${queue}`
    },
    finalizeTranslationProvider() {
      if (this.activeLanguage === 'en') return

      const provider = getLastTranslationProvider()
      this.translationProvider = provider?.status && provider.status !== 'none'
        ? provider
        : {
          key: 'article-cache',
          label: 'Article cache',
          cached: true,
          status: 'cached',
        }
    },
    closeLanguageMenu() {
      this.languageMenuOpen = false
    },
    selectLanguage(language) {
      this.activeLanguage = language
      this.languageMenuOpen = false
    },
    // Every assignment to `sourceArticle` / `article` is gated on the request that started it
    // still being the newest one, so a slow response for article A can never overwrite the
    // article B the user has already navigated to. Same counter covers the language switch.
    async loadArticle() {
      if (!this.articleUrl) {
        this.error = 'Missing article URL.'
        return
      }

      const requestId = ++this.articleRequestId
      this.loading = true
      this.error = ''
      this.articleBannerFailed = false
      const loadingId = await this.$store.dispatch('startGlobalLoading', `Loading ${this.articleSourceLabel} article...`)

      try {
        const article = await getNewsArticle(this.articleUrl, this.articleSource)
        if (requestId !== this.articleRequestId) return
        this.sourceArticle = article
        await this.applyArticleLanguage()
      } catch (error) {
        if (requestId !== this.articleRequestId) return
        this.error = `${this.articleSourceLabel} article is unavailable right now. Try opening the source link directly.`
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Article unavailable',
          description: this.error,
        })
      } finally {
        if (requestId === this.articleRequestId) this.loading = false
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    async loadOtherPosts() {
      this.otherPostsLoading = true

      try {
        const currentUrl = this.articleUrl
        const posts = await getWowheadFeedPosts(this.articleSource === 'wowhub' ? 'wowhub' : 'all', 'en')
        this.otherPosts = posts
          .filter((post) => post.link && post.link !== currentUrl)
          .slice(0, 5)
      } catch {
        this.otherPosts = []
      } finally {
        this.otherPostsLoading = false
      }
    },
    // Its own counter, separate from `articleRequestId`: translating a long article takes a
    // while, so switching UA -> RU (or loading a different article) mid-translation has to
    // discard the older result instead of letting whichever finishes last win.
    async applyArticleLanguage() {
      if (!this.sourceArticle) return

      const requestId = ++this.languageRequestId

      if (this.isNativeLanguageArticle || this.activeLanguage === 'en') {
        this.article = this.sourceArticle
        return
      }

      this.articleTranslating = true
      const translationLoadingId = await this.$store.dispatch('startGlobalLoading', `Translating article to ${this.activeLanguageLabel}...`)

      try {
        const translated = await this.translateArticle(this.sourceArticle, this.activeLanguage)
        if (requestId !== this.languageRequestId) return
        this.article = translated
      } finally {
        this.$store.dispatch('finishGlobalLoading', translationLoadingId)
        if (requestId === this.languageRequestId) {
          this.finalizeTranslationProvider()
          this.articleTranslating = false
        }
      }
    },
    async translateArticle(article, targetLang) {
      const collector = startTranslationCollection()

      try {
        const [[title, description], blocks] = await Promise.all([
          translateTextSegments([article.title, article.description], targetLang),
          mapWithConcurrency(article.blocks || [], 3, (block) => this.translateArticleBlock(block, targetLang)),
        ])

        setTranslationProvider(summarizeTranslationProviders(collector))

        return {
          ...article,
          title,
          description,
          blocks,
          originalTitle: article.title,
          originalDescription: article.description,
        }
      } finally {
        stopTranslationCollection(collector)
      }
    },
    async translateArticleBlock(block, targetLang) {
      if (block.type === 'heading') {
        return { ...block, text: await translateText(block.text, targetLang), originalText: block.text }
      }

      if (block.type === 'paragraph') {
        return { ...block, text: await translateText(block.text, targetLang), originalText: block.text }
      }

      if (block.type === 'html') {
        return { ...block, html: await this.translateHtml(block.html, targetLang), originalHtml: block.html }
      }

      if (block.type === 'quote' || block.type === 'bluepost') {
        return { ...block, html: await this.translateHtml(block.html || block.text || '', targetLang), originalHtml: block.html }
      }

      if (block.type === 'list') {
        const items = await mapWithConcurrency(block.items || [], 3, (item) => this.translateHtml(item, targetLang))
        return { ...block, items, originalItems: block.items }
      }

      return block
    },
    async translateHtml(html, targetLang) {
      if (!html) return ''

      const template = document.createElement('template')
      template.innerHTML = html
      const textNodes = []
      const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT)
      let node = walker.nextNode()

      while (node) {
        if (node.textContent.trim()) textNodes.push(node)
        node = walker.nextNode()
      }

      const batchSize = 8

      for (let index = 0; index < textNodes.length; index += batchSize) {
        const batch = textNodes.slice(index, index + batchSize)
        const translatedSegments = await translateTextSegments(batch.map((textNode) => textNode.textContent), targetLang)
        batch.forEach((textNode, batchIndex) => {
          textNode.textContent = translatedSegments[batchIndex]
        })
      }

      this.normalizeInlineLinkSpacing(template.content)
      return template.innerHTML
    },
    normalizeInlineLinkSpacing(root) {
      root.querySelectorAll('a').forEach((link) => {
        const previous = link.previousSibling
        const next = link.nextSibling

        if (previous?.nodeType === Node.TEXT_NODE && previous.textContent && !/\s$/.test(previous.textContent)) {
          previous.textContent += ' '
        }

        if (next?.nodeType === Node.TEXT_NODE && next.textContent && !/^\s/.test(next.textContent)) {
          next.textContent = ` ${next.textContent}`
        }
      })
    },
    handleArticleBannerError() {
      this.articleBannerFailed = true
    },
    async openRelatedArticle(url) {
      const requestId = ++this.relatedArticleRequestId
      this.relatedArticleOpen = true
      this.relatedArticleLoading = true
      this.relatedArticleError = ''
      this.relatedArticle = {
        title: '',
        imageUrl: '',
        url,
        blocks: [],
      }

      try {
        const article = await getNewsArticle(url, 'wowhub')
        if (requestId !== this.relatedArticleRequestId || !this.relatedArticleOpen) return
        this.relatedArticle = article
      } catch {
        if (requestId !== this.relatedArticleRequestId || !this.relatedArticleOpen) return
        this.relatedArticleError = 'The linked WoW Hub article is unavailable right now. You can still open its source in a new tab.'
      } finally {
        if (requestId === this.relatedArticleRequestId) this.relatedArticleLoading = false
      }
    },
    closeRelatedArticle() {
      this.relatedArticleRequestId += 1
      this.relatedArticleOpen = false
      this.relatedArticleLoading = false
      this.relatedArticleError = ''
    },
    handleDocumentKeydown(event) {
      if (event.key === 'Escape' && this.relatedArticleOpen) this.closeRelatedArticle()
    },
    persistNewsLanguage() {
      try {
        localStorage.setItem(NEWS_LANGUAGE_KEY, this.activeLanguage)
      } catch {
        // Ignore storage failures; language falls back to English.
      }
    },
    articleRoute(post) {
      return {
        path: '/news/article',
        query: { url: post.link, source: post.source || this.articleSource },
      }
    },
    formatDate: formatNewsDate,
  },
}
</script>
